<template>
    <div class="record-appraisal-detail loading-content-container">
        <div v-if="isLoading" class="loading-overlay d-flex justify-content-center pt-5">
            <csp-loading-indicator/>
        </div>

        <b-row v-if="!isLoading">
            <csp-desktop-info-card
                :cols="12"
                :form-context="appraisalFormContext"
                icon="inticon-autoclaim_billing"
                :title="`Forderung TÜV SÜD #${appraisalFormContext.data.appraisalNumber}`">
                <b-row>
                    <csp-desktop-info-card-element
                        class="col-6"
                        :form-context="appraisalFormContext"
                        label="Gutachtennummer"
                        property="appraisalNumber"
                        type="text"/>

                    <csp-desktop-info-card-element
                        class="col-6"
                        :form-context="appraisalFormContext"
                        label="Rechnungsnummer"
                        property="invoiceNumber"
                        type="text"/>

                    <csp-desktop-info-card-element
                        class="col-6"
                        :form-context="appraisalFormContext"
                        label="Rechnungsdatum"
                        property="invoiceDate"
                        type="date"/>

                    <csp-desktop-info-card-element
                        class="col-6"
                        :form-context="appraisalFormContext"
                        label="Gutachter"
                        property="appraiserName"
                        type="text"/>

                    <csp-desktop-info-card-element
                        class="col-6"
                        :form-context="appraisalFormContext"
                        label="E-Mail Gutachter"
                        property="appraiserEmail"
                        type="text"/>

                    <csp-desktop-info-card-element
                        class="col-6"
                        :form-context="appraisalFormContext"
                        label="Produktgruppe"
                        property="productGroup"
                        type="text"/>

                    <csp-desktop-info-card-element
                        class="col-6"
                        :form-context="appraisalFormContext"
                        label="Offener Betrag"
                        property="amountOpen"
                        type="currency"/>

                    <csp-desktop-info-card-element
                        class="col-6"
                        :form-context="appraisalFormContext"
                        label="Vorherige Forderungsnummer"
                        property="previousAppraisalNumber"
                        type="text"/>
                </b-row>
            </csp-desktop-info-card>
        </b-row>

        <csp-table
            v-if="!isLoading"
            ref="lineItemsTable"
            class="mt-4"
            :disable-filters="true"
            icon="inticon-autoclaim_damage"
            :items-ajax-url="lineItemsAjaxUrl"
            :rows-selectable="false"
            :scrollable="true"
            table-headline="Posten"
            @fields-loaded="(fields) => lineItemColumns = fields.map((f) => f.key)">
            <template v-for="col in lineItemColumns" v-slot:[`cell(${col})`]="{ data }">
                <csp-text-with-tooltip
                    :key="col"
                    :delay="tooltipDelay"
                    :text="String(data[col])"
                    :tooltip="String(data[col])"
                    truncate/>
            </template>
            <template v-slot:cell(changelog)="{ data }">
                <csp-changelog-modal :changelog-url="data.changelog.changelogUrl"/>
            </template>
        </csp-table>

        <b-row v-if="!isLoading && canShowDigitalMailboxEmails" class="mt-4">
            <b-col cols="12">
                <csp-digital-mailbox-email-list-table
                    :items-ajax-url="itemsAjaxUrl"
                    table-headline="Zugeordnete E-Mails">
                    <template v-slot:cell(actions)="{ data }">
                        <div class="table-actions">
                            <a
                                v-if="data.actions.attachmentCount"
                                title="Anhänge anzeigen"
                                @click.stop="openAttachmentModal(data.actions.emailId)">
                                <i class="inticon-autoclaim_files"/>
                            </a>
                        </div>
                    </template>
                </csp-digital-mailbox-email-list-table>
            </b-col>
        </b-row>

        <csp-email-attachment-modal
            :email-id="attachmentModalEmailId"
            :show="showAttachmentModal"
            @hidden="onAttachmentModalHidden"/>
    </div>
</template>

<script>
    import Container from '@services/Container';
    import FormContext from '@desktop/components/FormContext';
    import FormDataMixin from '@desktop/components/mixins/FormDataMixin';
    import EmailAttachmentModal from '@desktop/components/digital-mailbox/EmailAttachmentModal.vue';
    import EmailListTable from '@desktop/components/digital-mailbox/EmailListTable.vue';
    import TextWithTooltip from '@desktop/components/TextWithTooltip.vue';

    const container = Container.getInstance();
    const recordRepository = container.getDesktopRecordRepository();

    export default {
        name: 'AppraisalDetail',
        components: {
            'csp-digital-mailbox-email-list-table': EmailListTable,
            'csp-email-attachment-modal': EmailAttachmentModal,
            'csp-text-with-tooltip': TextWithTooltip,
        },
        mixins: [FormDataMixin],
        data() {
            return {
                attachmentModalEmailId: null,
                tooltipDelay: 600,
                canShowDigitalMailboxEmails: false,
                isLoading: true,
                lineItemColumns: [],
                showAttachmentModal: false,
                initialFormContexts: {
                    recordAppraisalDetail: new FormContext({
                        name: 'recordAppraisalDetail',
                        handleEditModeByQuery: false,
                    }),
                },
            };
        },
        computed: {
            appraisalId() {
                return parseInt(this.$route.params.appraisalId);
            },
            appraisalFormContext() {
                return this.initialFormContexts.recordAppraisalDetail;
            },
            lineItemsAjaxUrl() {
                return `/api/desktop/records/${this.recordId}/appraisals/${this.appraisalId}/line-items`;
            },
            itemsAjaxUrl() {
                return `/api/desktop/records/${this.recordId}/appraisals/${this.appraisalId}/digital-mailbox/emails`;
            },
            recordId() {
                return parseInt(this.$route.params.recordId);
            },
        },
        created() {
            this.loadFormData(() => recordRepository.loadAppraisalDetail(this.recordId, this.appraisalId))
                .then((response) => {
                    this.canShowDigitalMailboxEmails =
                        response?.data?.meta?.permissions?.canShowDigitalMailboxEmails
                        ?? false;
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        methods: {
            onAttachmentModalHidden() {
                this.showAttachmentModal = false;
            },
            openAttachmentModal(emailId) {
                this.attachmentModalEmailId = emailId;
                this.showAttachmentModal = true;
            },
        },
    };
</script>
